import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type Keycloak from 'keycloak-js';
import type { AuthConfig } from '../config/types';
import { type AuthUser, createKeycloak, userFromKeycloak } from './keycloak';

interface AuthState {
  authenticated: boolean;
  user: AuthUser | null;
  keycloak: Keycloak | null;
  logout: () => void;
}

const defaultState: AuthState = {
  authenticated: false,
  user: null,
  keycloak: null,
  logout: () => {}
};

const AuthContext = createContext<AuthState>(defaultState);

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({
  config,
  children
}: {
  config: AuthConfig | null;
  children: ReactNode;
}) {
  const [state, setState] = useState<AuthState>(defaultState);

  useEffect(() => {
    // No auth configured = testing mode (e.g. deal.do.place reference instance).
    if (!config) {
      setState({
        authenticated: true,
        user: { id: 'anon', username: 'anonymous', roles: [] },
        keycloak: null,
        logout: () => {}
      });
      return;
    }

    const kc = createKeycloak(config);
    let refreshTimer: number | undefined;

    kc.init({
      onLoad: 'login-required',
      checkLoginIframe: false,
      pkceMethod: 'S256'
    })
      .then((authenticated) => {
        setState({
          authenticated,
          user: userFromKeycloak(kc),
          keycloak: kc,
          logout: () => kc.logout()
        });

        if (authenticated) {
          refreshTimer = window.setInterval(() => {
            kc.updateToken(60).catch(() => kc.login());
          }, 30_000);
        }
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('Keycloak init failed', err);
      });

    return () => {
      if (refreshTimer !== undefined) window.clearInterval(refreshTimer);
    };
  }, [config]);

  return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>;
}
